import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Currency } from '@sorare/core/src/__generated__/globalTypes';
import Button from '@sorare/core/src/atoms/buttons/Button';
import CloseButton from '@sorare/core/src/atoms/buttons/CloseButton';
import IconButton from '@sorare/core/src/atoms/buttons/IconButton';
import { Plus } from '@sorare/core/src/atoms/icons/Plus';
import { Drawer } from '@sorare/core/src/atoms/layout/Drawer';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LinkOverlay } from '@sorare/core/src/atoms/navigation/Box';
import { BodyM, HeadlineS } from '@sorare/core/src/atoms/typography';
import ConversionCredit from '@sorare/core/src/components/wallet/BankEthAccounting/ConversionCredits/ConversionCredit';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';

import { usePaymentContext } from 'components/buyActions/Context';
import { PaymentBoxAmountWithConversion } from 'components/buyActions/PaymentBox/AmountWithConversion';

const ConversionCreditText = styled(BodyM)`
  background: linear-gradient(330deg, #048a3d 2.3%, #bff6d6 95.33%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const StyledDrawer = styled(Drawer)`
  background-color: var(--c-neutral-200);
`;

const Header = styled.div`
  display: grid;
  padding: var(--double-unit);
  border-bottom: 1px solid var(--c-neutral-400);
  grid-template-columns: 40px 1fr 40px;
  align-items: center;
  text-align: center;
`;

const Item = styled(Vertical)`
  border-radius: var(--double-unit);
  background-color: var(--c-neutral-300);
  padding: var(--double-unit);
`;

const Content = styled(Vertical)`
  padding: var(--double-unit);
`;

export const ConversionCreditRow = ({
  paymentBoxAmountWithConversionProps,
}: {
  paymentBoxAmountWithConversionProps: {
    hideExponent?: boolean;
    primaryCurrency?: Currency;
    inline?: boolean;
    reverse?: boolean;
  };
}) => {
  const [open, setOpen] = useState(false);

  const {
    conversionCredit,
    conversionCredits,
    conversionCreditMonetaryAmount,
    referenceCurrency,
    setConversionCredit,
  } = usePaymentContext();

  const isTabletAndAbove = useIsTabletAndAbove();

  return (
    <>
      {conversionCredit ? (
        <>
          <Horizontal center>
            <IconButton
              onClick={() => {
                setConversionCredit(undefined);
              }}
              smaller
              icon={faXmark}
              color="transparent"
            />
            <ConversionCreditText>
              <FormattedMessage
                id="PaymentBox.creditApplied"
                defaultMessage="Credit applied"
              />
            </ConversionCreditText>
          </Horizontal>
          <PaymentBoxAmountWithConversion
            monetaryAmount={conversionCreditMonetaryAmount}
            {...paymentBoxAmountWithConversionProps}
            MainAmountComponent={ConversionCreditText}
          />
        </>
      ) : (
        <>
          <Horizontal>
            <IconButton smaller color="transparent">
              <Plus fill="currentColor" />
            </IconButton>
            <ConversionCreditText>
              <FormattedMessage
                id="PaymentBox.applyCredits"
                defaultMessage="Apply Credits ({count} available)"
                values={{ count: conversionCredits?.length }}
              />
            </ConversionCreditText>
          </Horizontal>
          <LinkOverlay
            onClick={() => {
              setOpen(true);
            }}
          />
        </>
      )}
      <StyledDrawer
        blur
        side={isTabletAndAbove ? 'right' : 'bottom'}
        open={open}
        onBackdropClick={() => setOpen(false)}
      >
        <Header>
          <div />
          <HeadlineS>
            <FormattedMessage
              id="ConversionCreditRow.drawer.title"
              defaultMessage="Apply credit"
            />
          </HeadlineS>
          <CloseButton onClose={() => setOpen(false)} />
        </Header>
        <Content>
          {(conversionCredits || []).map(conversionCreditItem => {
            return (
              <Item key={conversionCreditItem.id}>
                <ConversionCredit
                  referenceCurrency={referenceCurrency}
                  conversionCredit={conversionCreditItem}
                  {...(conversionCreditItem.id === conversionCredit?.id
                    ? {
                        spentConversionCreditMonetaryAmount:
                          conversionCreditMonetaryAmount,
                      }
                    : {})}
                />
                {conversionCreditItem.disabled ? (
                  <Button disabled color="secondary" size="small">
                    <FormattedMessage
                      id="ConversionCreditRow.drawer.AlreadyApplied"
                      defaultMessage="Already applied"
                    />
                  </Button>
                ) : (
                  <Button
                    size="small"
                    onClick={() => {
                      setConversionCredit(conversionCreditItem);
                      setOpen(false);
                    }}
                  >
                    <FormattedMessage
                      id="ConversionCreditRow.drawer.apply"
                      defaultMessage="Apply"
                    />
                  </Button>
                )}
              </Item>
            );
          })}
        </Content>
      </StyledDrawer>
    </>
  );
};

export default ConversionCreditRow;
